import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Users, Library as LibraryIcon, Bell, LogOut, Menu, Star,
  Heart, Radio, Sparkles, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { Wordmark } from "@/components/SiteHeader";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDateShort } from "@/lib/format";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/agenda", label: "Agenda", icon: CalendarDays },
  { to: "/admin/clientes", label: "Clientes", icon: Users },
  { to: "/admin/biblioteca", label: "Biblioteca Musical", icon: LibraryIcon },
];

const SERVICE_NAV = [
  { service: "cerimonia", label: "Cerimônia", icon: Heart },
  { service: "banda", label: "Banda / Receptivo", icon: Radio },
  { service: "astrolive", label: "AstroLive", icon: Sparkles },
];

const NavItems = ({ onNavigate }) => (
  <nav className="space-y-1">
    {NAV.map(({ to, label, icon: Icon }) => (
      <NavLink
        key={to}
        to={to}
        onClick={onNavigate}
        data-testid={`admin-nav-${label.toLowerCase().split(" ")[0]}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
            isActive
              ? "bg-white/10 text-white before:absolute"
              : "text-white/60 hover:bg-white/5 hover:text-white"
          }`
        }
      >
        <Icon className="h-4.5 w-4.5" strokeWidth={1.75} />
        {label}
      </NavLink>
    ))}

    <p className="px-3 pb-1 pt-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/30">Por serviço</p>
    {SERVICE_NAV.map(({ service, label, icon: Icon }) => (
      <NavLink
        key={service}
        to={`/admin/clientes?service=${service}`}
        onClick={onNavigate}
        data-testid={`admin-nav-servico-${service}`}
        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/60 transition-colors hover:bg-white/5 hover:text-white"
      >
        <Icon className="h-4.5 w-4.5" strokeWidth={1.75} />
        {label}
      </NavLink>
    ))}
  </nav>
);

export default function AdminLayout() {
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/admin/notifications");
      setNotifs(data.notifications || []);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  };

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 20000);
    return () => clearInterval(t);
  }, []);

  const logout = () => {
    localStorage.removeItem("lc_admin_token");
    localStorage.removeItem("lc_admin_email");
    navigate("/admin", { replace: true });
  };

  const markAllRead = async () => {
    await api.post("/admin/notifications/read-all");
    loadNotifs();
  };

  const openNotif = (n) => {
    setNotifOpen(false);
    if (n.event_id) navigate(`/admin/clientes/${n.event_id}`);
  };

  const unread = notifs.filter((n) => !n.read).length;

  const Sidebar = () => (
    <div className="flex h-full flex-col bg-[color:var(--brand-graphite)] p-4">
      <div className="mb-8 px-2 pt-2">
        <Wordmark dark size={38} tagline="Painel administrativo" />
      </div>
      <NavItems onNavigate={() => setMenuOpen(false)} />
      <button onClick={logout} data-testid="admin-logout" className="mt-auto flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/60 transition-colors hover:bg-white/5 hover:text-white">
        <LogOut className="h-4 w-4" /> Sair
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[color:var(--brand-paper)]">
      <aside className="fixed inset-y-0 left-0 hidden w-60 lg:block">
        <Sidebar />
      </aside>

      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 border-b border-[color:var(--brand-border)] bg-white">
          <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-[color:var(--brand-champagne)] to-transparent opacity-80" />
          <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button className="rounded-lg p-2 text-[color:var(--brand-graphite)] lg:hidden" data-testid="admin-menu-toggle">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <Sidebar />
              </SheetContent>
            </Sheet>
            <div className="lg:hidden"><Wordmark size={30} tagline={null} /></div>
            <span className="hidden font-serif text-lg font-semibold italic text-[color:var(--brand-graphite)] lg:inline">Painel</span>
          </div>

          <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
            <DropdownMenuTrigger asChild>
              <button data-testid="admin-notifications-bell" className="relative rounded-lg p-2 text-[color:var(--brand-graphite)] hover:bg-[color:var(--brand-paper)]">
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[color:var(--brand-marsala)] px-1 text-[10px] font-semibold text-white">{unread}</span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 p-0">
              <div className="flex items-center justify-between border-b border-[color:var(--brand-border)] p-3">
                <span className="text-sm font-semibold">Notificações</span>
                {unread > 0 && <button onClick={markAllRead} className="text-xs text-[color:var(--brand-marsala)] hover:underline">Marcar todas lidas</button>}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifs.length === 0 ? (
                  <p className="p-6 text-center text-sm text-[color:var(--brand-muted)]">Nenhuma notificação.</p>
                ) : (
                  notifs.map((n) => (
                    <button
                      key={n.id}
                      data-testid={`admin-notif-${n.id}`}
                      onClick={() => openNotif(n)}
                      className={`flex w-full items-start gap-2 border-b border-[color:var(--brand-border)] p-3 text-left transition-colors hover:bg-[color:var(--brand-paper)] ${!n.read ? "bg-[color:var(--brand-marsala-soft)]" : ""}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-1.5 text-sm font-medium text-[color:var(--brand-graphite)]">
                          {n.type === "new_repertoire" ? <Star className="h-3.5 w-3.5 text-[color:var(--brand-marsala)]" /> : <Bell className="h-3.5 w-3.5" />}
                          {n.type === "new_repertoire" ? "Novo repertório recebido" : "Solicitação de reabertura"}
                        </p>
                        <p className="mt-0.5 text-xs text-[color:var(--brand-muted)]">{n.message}</p>
                        {n.scope && (
                          <span className="mt-1 inline-block rounded-full bg-[color:var(--brand-champagne-soft)] px-2 py-0.5 text-[10px] font-semibold text-[color:var(--brand-graphite)]">{n.scope}</span>
                        )}
                        <p className="mt-1 text-[11px] text-[color:var(--brand-muted)]">{n.local} · {formatDateShort(n.date)}</p>
                      </div>
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-[color:var(--brand-muted)]" />
                    </button>
                  ))
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
